// src/pages/driver/DriverPaymentsPage.js
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  TextField,
  InputAdornment,
  styled
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { getPaymentTransactions } from '../../api/paymentTransactionAPI';
import DriverPaymentTransactionList from '../../components/payment/DriverPaymentTransactionList';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

// Styled components for consistent sizing
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minHeight: 80,
  width: '100%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  minHeight: 80,
  width: '100%',
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  width: '100%',
  '& .MuiSelect-select': {
    paddingRight: '32px !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}));

const paymentStatuses = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'refunded', label: 'Refunded' }
];

const DriverPaymentsPage = () => {
  const [filters, setFilters] = useState({
    status: '',
    startDate: null,
    endDate: null,
    minAmount: '',
    maxAmount: ''
  });

  // Fetch payment transactions - Driver only sees their own transactions (backend handles filtering)
  const { data: paymentTransactions, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['driver-payment-transactions', filters],
    queryFn: () => getPaymentTransactions({
      // Driver-specific filters - no company/site/charger as driver only sees their own
      status: filters.status || undefined,
      start_date: filters.startDate ? filters.startDate.toISOString() : undefined,
      end_date: filters.endDate ? filters.endDate.toISOString() : undefined,
      min_amount: filters.minAmount ? parseFloat(filters.minAmount) : undefined,
      max_amount: filters.maxAmount ? parseFloat(filters.maxAmount) : undefined
    })
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      startDate: null,
      endDate: null,
      minAmount: '',
      maxAmount: ''
    });
  };

  // Menu props for consistent dropdown styling
  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    MenuListProps: {
      disableAutoFocusItem: true,
      style: { paddingTop: 0, paddingBottom: 0 }
    },
  };

  // Common style for input fields
  const inputStyle = {
    height: 56,
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading payment transactions'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <PaymentIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">My Payment History</Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* Simplified Filters Panel for Drivers */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filter My Payments</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={handleResetFilters} size="small">
            Reset
          </Button>
        </Box>
        
        <Grid container spacing={3}>
          {/* First Row of Filters */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Payment Status Filter */}
              <Grid item xs={12} sm={6} md={2.4}>
                <StyledFormControl>
                  <InputLabel id="status-filter-label" shrink>Payment Status</InputLabel>
                  <StyledSelect
                    labelId="status-filter-label"
                    value={filters.status}
                    label="Payment Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    MenuProps={menuProps}
                    sx={inputStyle}
                    displayEmpty
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {paymentStatuses.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </StyledFormControl>
              </Grid>

              {/* Start Date Filter */}
              <Grid item xs={12} sm={6} md={2.4}>
                <StyledTextField
                  label="From Date"
                  type="datetime-local"
                  value={filters.startDate ? filters.startDate.toISOString().slice(0, 16) : ''}
                  onChange={(e) => handleDateChange('startDate', e.target.value ? new Date(e.target.value) : null)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonthIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    style: inputStyle
                  }}
                  fullWidth
                />
              </Grid>

              {/* End Date Filter */}
              <Grid item xs={12} sm={6} md={2.4}>
                <StyledTextField
                  label="To Date"
                  type="datetime-local"
                  value={filters.endDate ? filters.endDate.toISOString().slice(0, 16) : ''}
                  onChange={(e) => handleDateChange('endDate', e.target.value ? new Date(e.target.value) : null)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonthIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    style: inputStyle
                  }}
                  fullWidth
                />
              </Grid>

              {/* Min Amount Filter */}
              <Grid item xs={12} sm={6} md={2.4}>
                <StyledTextField
                  label="Min Amount"
                  type="number"
                  value={filters.minAmount}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                  placeholder="0.00"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    style: inputStyle,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                />
              </Grid>

              {/* Max Amount Filter */}
              <Grid item xs={12} sm={6} md={2.4}>
                <StyledTextField
                  label="Max Amount"
                  type="number"
                  value={filters.maxAmount}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                  placeholder="100.00"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    style: inputStyle,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Payment Transactions List - Using Driver-specific component */}
      <Box>
        <DriverPaymentTransactionList 
          paymentTransactions={paymentTransactions || []}
          isLoading={isLoading}
          isError={isError}
          error={error}
        />
      </Box>
    </Box>
  );
};

export default DriverPaymentsPage;